import React from "react";
import {
  CountByEntityStatus,
  CompletionRateByRole,
  GeneralTask,
  OverdueItem,
  ProjectTask,
} from "./UserHomeApi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faClipboardCheck,
  faClockRotateLeft,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import {
  GeneralTasksTable,
  OverdueTable,
  ProjectTasksTable,
  ActivityFeed,
} from "./UserHomeList";

export interface DashboardProps {
  generalTasks: GeneralTask[];
  projectTasks: ProjectTask[];
  overdueProject: OverdueItem[];
  overdueAll: OverdueItem[];
  completionRate: CompletionRateByRole[];
  countsByEntityStatus: CountByEntityStatus[];
  activity: any[];
  topPriorities: Array<{ title: string; due_date: string; priority: string | null }>;
  pendingAssignedByMe: { count: number };
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
}

export const UserHomeDashboard: React.FC<DashboardProps> = ({
  generalTasks,
  projectTasks,
  overdueProject,
  overdueAll,
  completionRate,
  countsByEntityStatus,
  activity,
  topPriorities,
  pendingAssignedByMe,
  loading,
  error,
  onRetry,
}) => {
  const totalAssigned =
    countsByEntityStatus?.reduce((acc, c) => acc + (c.count || 0), 0) || 0;
  const myCompletion = completionRate?.[0]?.completion_rate ?? 0;

  return (
    <div className="space-y-4">
      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 text-red-700 px-4 py-3">
          {error}
          {onRetry && (
            <button onClick={onRetry} className="ml-3 underline">
              Retry
            </button>
          )}
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={faChartLine} label="Total Assigned" value={totalAssigned} hint="All entity types" />
        <KpiCard icon={faClipboardCheck} label="Completion Rate" value={`${myCompletion}%`} hint="Role-based" />
        <KpiCard icon={faTriangleExclamation} label="Overdue (All)" value={overdueAll?.length ?? 0} hint="Across entities" />
        <KpiCard icon={faClockRotateLeft} label="Pending Assigned By Me" value={pendingAssignedByMe?.count ?? 0} hint="Delegated tasks" />
      </div>

      {/* Top Priorities */}
      <div className="rounded-2xl border bg-white shadow-sm">
        <div className="px-4 py-3 border-b">
          <h3 className="font-semibold">Top Priorities (Next 7 Days)</h3>
        </div>
        <ul className="divide-y">
          {topPriorities.map((p, idx) => (
            <li key={idx} className="px-4 py-3 flex items-center justify-between">
              <span className="font-medium">{p.title}</span>
              <span className="text-sm text-slate-600">
                {new Date(p.due_date).toLocaleString()}
              </span>
            </li>
          ))}
          {topPriorities.length === 0 && (
            <li className="px-4 py-6 text-center text-slate-500">No upcoming priorities</li>
          )}
        </ul>
      </div>

      {/* Grids: General / Project / Overdue / Activity */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {/* Actions turned on for both tables */}
        <GeneralTasksTable data={generalTasks} withActions onRetry={onRetry} />
        <ProjectTasksTable data={projectTasks} withActions onRetry={onRetry} />
        <OverdueTable data={overdueProject} title="Overdue (Project Tasks)" />
        <OverdueTable data={overdueAll} title="Overdue (All Entities)" />
        <ActivityFeed data={activity} />
      </div>

      {loading && <div className="text-center text-slate-500">Loading…</div>}
    </div>
  );
};

const KpiCard: React.FC<{
  icon: any;
  label: string;
  value: number | string;
  hint?: string;
}> = ({ icon, label, value, hint }) => (
  <div className="rounded-2xl border bg-white shadow-sm p-4">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
        <p className="text-2xl font-semibold mt-1">{value}</p>
      </div>
      <div className="w-10 h-10 rounded-xl border flex items-center justify-center">
        <FontAwesomeIcon icon={icon} />
      </div>
    </div>
    {hint && <p className="text-xs text-slate-500 mt-2">{hint}</p>}
  </div>
);